import { lazy } from 'react'
import { inited, stateRef } from './states'
import { isString } from './utils'
import type { ComponentType, LazyExoticComponent } from 'react'
import type { Vars } from './stateTypes'

export { hasMicroApp } from './states'

/**
 * Check if the micro-app is inited and exported
 * @param microAppIdOrName - micro-app name or id as string, or object with name or id
 * @returns exported as boolean, undefined if no micro-app inited or exported
 */
export const hasMicroAppExported = (microAppIdOrName: string | { name: string } | { id: string }): boolean | undefined => {
  if (!Array.isArray(stateRef.current.initedMicroApps)) {
    return undefined
  }
  if (isString(microAppIdOrName)) {
    return stateRef.current.initedMicroApps.some(microApp => (
      microApp.id === microAppIdOrName || microApp.appName === microAppIdOrName
    ))
  }
  if ('id' in microAppIdOrName) {
    return stateRef.current.initedMicroApps.some(microApp => microApp.id === microAppIdOrName.id)
  }
  if ('name' in microAppIdOrName) {
    return stateRef.current.initedMicroApps.some(microApp => microApp.appName === microAppIdOrName.name)
  }
}

export type GetMicroAppVars = ((name?: string) => Vars | undefined) | (() => Record<string, Vars>)
/**
 * Return the sparky variables by given microAppName, and it will give all micro-apps' variable if no name is given.
 * It return undefined for micro-apps' without any variables defined.
 * @param name - micro-app name
 * @returns micro-app sparky variables
 */
export const getMicroAppVars: GetMicroAppVars = (name?: string) => {
  return name ? inited.settings?.variables?.[name] : undefined
}

/**
 * Return the if the namespace or micro-app name has something exposed
 * @param NsOrAppNameId - expose namespace or micro-app name/id
 * @returns if the namespace exist
 */
export const hasExposedNamespace = (NsOrAppNameId: string): boolean => {
  const exposes = stateRef.current.exposes || inited.settings?.exposes || {}
  return Boolean(exposes[NsOrAppNameId])
}

export const getExposedNamespace = (appName: string): any => {
  const exposes = stateRef.current.exposes || inited.settings?.exposes
  const exposed = exposes?.[appName]
  return exposed
}

const getExposedInternal = (appName: string, exposeName: string): any => {
  const exposes = stateRef.current.exposes || inited.settings?.exposes
  const exposed = exposes?.[appName]?.[exposeName]
  return exposed
}

/**
 * Async import something exposed by another micro-app, and returned as a promise,
 * ready to be used by `React.lazy(importExposed('micro-app', 'expose-name'))`
 * The returned promise will wait for framework and micro-app to be loaded.
 * @param appName - micro-app name
 * @param exposeName - the key of the exposes object
 * @param exportName - the name of the named export
 * @returns the resolved exported value
 */
export const importExposed = async <T = any>(appName: string, exposeName: string, exportName?: string): Promise<T> => {
  const exposes = inited.settings?.exposes
  let fn = getExposedInternal(appName, exposeName)
  if (fn === undefined && !inited.result) {
    await inited
    fn = exposes[appName]?.[exposeName]
  }
  const promise = fn?.() || fn
  if (!exportName || !promise) {
    return promise
  }
  return promise.then?.((result: any) => result[exportName] ?? promise[exportName]) ?? promise[exportName]
}

export type ModuleConvertFn<T> = (module: any) => T | Promise<T>

/**
 * Async import something exposed by another micro-app wrapped with react lazy,
 * which will auto convert to { default: component } format,
 * ready to be used by `const LazyComp = lazyImportExposed('micro-app', 'expose-name', 'default')`
 * The returned promise will wait for framework and micro-app to be loaded.
 * @param appName - micro-app name
 * @param exposeName - the key of the exposes object
 * @param exportNameOrFn - the name of the named export, or callback function to convert
 * @returns the React.lazy component of the exported value
 */
export const lazyImportComponent = <T extends ComponentType<any>>(appName: string, exposeName: string, exportNameOrFn?: string | ModuleConvertFn<T>): LazyExoticComponent<T> => {
  const ensureDefault = (module: any) => (module?.default ? module : { default: module })
  if (!exportNameOrFn || exportNameOrFn === 'default') {
    return lazy<T>(() => importExposed(appName, exposeName).then(ensureDefault))
  }
  if (typeof exportNameOrFn === 'string') {
    return lazy<T>(() => importExposed(appName, exposeName).then((module: any) => ensureDefault(module?.[exportNameOrFn!])))
  }
  return lazy<T>(() => importExposed(appName, exposeName).then(exportNameOrFn).then(ensureDefault))
}
/**
 * Sync import something exposed by another micro-app, and return the exposed value directly.
 * If the framework or micro-app is not loaded yet, it will simply **throw an error**.
 * If the exposed returned a promise, the same promise will be returned by the function without resolve it.
 * So it CANNOT directly used by `React.lazy`
 * @param appName - micro-app name
 * @param exposeName - the key of the exposes object
 * @returns the exported value (without resolve)
 */
export const getExposedSync = <T = any>(appName: string, exposeName: string): T => {
  const exposed = getExposedInternal(appName, exposeName)
  if (exposed === undefined && !inited.result) {
    throw new Error('framework not inited yet')
  }
  return exposed as T
}
